import { jsx as _jsx } from "react/jsx-runtime";
// SyncContext — React Context + Refs for Multi-Chart Synchronization
// Implements the SyncContextValue interface from types/sync.ts
// Uses injected adapters for ALL external dependencies (ClockAdapter, BroadcastChannelAdapter)
// NO direct Web API access - all external dependencies must be injected
import React, { createContext, useContext, useRef, useCallback, useEffect, useState, useMemo, } from 'react';
import { DEFAULT_THROTTLE_POLICY, } from '../types/sync';
// ============================================
// Context Creation
// ============================================
const SyncContext = createContext(null);
// Message-type prefix → ThrottlePolicy domain (spec §3).
// Unknown prefixes (e.g. 'indicator', 'sync') fall back to the drawing budget.
const CROSS_TAB_DOMAIN_MAP = {
    'visible-range': 'visibleRange',
    'crosshair': 'crosshair',
    'drawing': 'drawing',
    'symbol-link': 'symbolLink',
    'timeframe': 'timeframe',
    'chart-type': 'chartType',
    'drawing-tool': 'drawingTool',
    'workspace': 'workspace',
};
function stampEchoMeta(payload, meta) {
    return payload && typeof payload === 'object'
        ? { ...payload, meta: { sequence: meta.sequence, sourceChartId: meta.sourceChartId } }
        : payload;
}
export const useSyncContext = () => {
    const context = useContext(SyncContext);
    if (!context) {
        throw new Error('useSyncContext must be used within SyncProvider');
    }
    return context;
};
// P2: normalize the two cross-tab drawing payload shapes into one
// DrawingOperation. Live senders (syncDrawing) broadcast the op itself;
// legacy senders post { drawing, action }. Returns null for foreign payloads.
function normalizeCrossTabDrawingOp(payload, sourceTabId, now) {
    if (!payload || typeof payload !== 'object')
        return null;
    if (typeof payload.drawingId === 'string'
        && (payload.type === 'create' || payload.type === 'update' || payload.type === 'delete' || payload.type === 'move-point')) {
        return {
            ...payload,
            timestamp: typeof payload.timestamp === 'number' ? payload.timestamp : now,
            authorId: payload.authorId || sourceTabId || 'other-tab',
        };
    }
    if (payload.drawing && (payload.action === 'add' || payload.action === 'update' || payload.action === 'delete')) {
        const drawing = payload.drawing;
        const action = payload.action;
        return {
            id: `op-${action}-${drawing.id}-${now}`,
            drawingId: drawing.id,
            type: action === 'add' ? 'create' : action,
            version: drawing.versionVector || {},
            data: action === 'delete' ? undefined : drawing,
            timestamp: now,
            authorId: sourceTabId || 'other-tab',
            syncInLayout: drawing?.syncInLayout,
        };
    }
    return null;
}
export const SyncProvider = ({ children, throttlePolicy, workspaceId, adaptiveThrottle = true, adaptiveThrottleThreshold = 4, clockAdapter, broadcastChannelAdapter, storageAdapter, }) => {
    // Validate required adapters
    if (!clockAdapter) {
        throw new Error('SyncProvider requires clockAdapter prop');
    }
    if (!broadcastChannelAdapter) {
        throw new Error('SyncProvider requires broadcastChannelAdapter prop');
    }
    // --- Refs for mutable state (avoid re-renders on high-frequency updates) ---
    const stateRef = useRef({
        charts: new Map(),
        symbolLinkGroups: new Map(),
        drawingState: new Map(),
        drawingTombstones: new Map(),
        throttlePolicy: { ...DEFAULT_THROTTLE_POLICY, ...throttlePolicy },
        crossTabChannel: null,
        workspaceId: workspaceId || null,
        sequenceCounter: 0,
        activeChartId: null,
        activeChartSubscriptions: new Set(),
        groupSymbolCallbacks: new Map(),
        drawingConflictCallbacks: new Set(),
        adaptiveThrottle,
        adaptiveThrottleThreshold,
        indicatorState: new Map(),
    });
    // --- Force update for React subscriptions (low-frequency) ---
    const [, forceUpdate] = useState(0);
    const triggerUpdate = useCallback(() => forceUpdate(n => n + 1), []);
    // --- Throttling / Debouncing state ---
    const pendingTasksRef = useRef(new Map());
    // Last cross-tab postMessage time per policy domain (spec §3: separate cross-tab budget, Q8)
    const lastCrossTabSentRef = useRef(new Map());
    // Last sequence applied per target chart+domain (spec §5 loop-guard, A3).
    // Key: `${chartId}:${domain}` — e.g. 'chart-B:visibleRange'.
    const lastAppliedSequenceRef = useRef(new Map());
    const getThrottleMs = useCallback((domain) => {
        const policy = stateRef.current.throttlePolicy;
        const domainPolicy = policy[domain];
        let baseMs;
        if (typeof domainPolicy === 'number') {
            baseMs = domainPolicy;
        }
        else if (domainPolicy && typeof domainPolicy.throttleMs === 'number') {
            baseMs = domainPolicy.throttleMs;
        }
        else {
            // Fallback values
            if (domain === 'visibleRange')
                baseMs = 16;
            else if (domain === 'crosshair')
                baseMs = 16;
            else if (domain === 'drawing')
                baseMs = 50;
            else if (domain === 'workspace')
                baseMs = 1000;
            else if (domain === 'crossTab')
                baseMs = 100;
            else
                baseMs = 0;
        }
        // Adaptive throttle: scale up when chart count exceeds threshold
        if (stateRef.current.adaptiveThrottle && baseMs > 0) {
            const chartCount = stateRef.current.charts.size;
            const threshold = stateRef.current.adaptiveThrottleThreshold;
            if (threshold > 0 && chartCount > threshold) {
                const scaleFactor = Math.ceil(chartCount / threshold);
                baseMs = Math.min(baseMs * scaleFactor, 200); // clamp at 200ms
            }
        }
        return baseMs;
    }, []);
    // Cross-tab leg budget per spec §3 (Q8). Unset → 50ms; 0 = unthrottled.
    // Adaptive scaling does NOT apply here — IPC cost dominates, not fan-out size.
    const getCrossTabThrottleMs = useCallback((domain) => {
        const policy = stateRef.current.throttlePolicy;
        const domainPolicy = policy[domain];
        if (domainPolicy && typeof domainPolicy.crossTabThrottleMs === 'number') {
            return domainPolicy.crossTabThrottleMs;
        }
        return 50;
    }, []);
    // Loop-guard probe (spec §5, A3). Returns true = DROP: the call re-emits a
    // remotely-applied sequence (stale echo or self re-send), not fresh intent.
    // Calls WITHOUT meta always proceed (fresh local gestures carry no stamp).
    const isEchoEvent = useCallback((chartId, domain, meta) => {
        if (!meta || typeof meta.sequence !== 'number')
            return false;
        if (meta.sourceChartId === chartId)
            return true;
        const lastSeq = lastAppliedSequenceRef.current.get(`${chartId}:${domain}`) ?? 0;
        return meta.sequence <= lastSeq;
    }, []);
    // --- Helpers ---
    const getNextSequence = useCallback(() => {
        return ++stateRef.current.sequenceCounter;
    }, []);
    const getTime = useCallback(() => {
        // clockAdapter is required - no fallback to performance.now() or Date.now()
        if (!clockAdapter) {
            throw new Error('SyncContext requires clockAdapter to be provided');
        }
        return clockAdapter.now();
    }, [clockAdapter]);
    const createMeta = useCallback((sourceChartId, targetChartIds, groupId) => ({
        sourceChartId,
        targetChartIds,
        groupId,
        timestamp: getTime(),
        sequence: getNextSequence(),
    }), [getNextSequence, getTime]);
    const broadcast = useCallback((type, payload, meta) => {
        const envelope = { type, payload, meta };
        const { charts, throttlePolicy } = stateRef.current;
        // Determine target charts
        let targets = meta.targetChartIds
            ? meta.targetChartIds
            : Array.from(charts.keys()).filter(id => id !== meta.sourceChartId);
        // Filter targets based on Symbol Link Group for visible-range, crosshair,
        // symbol-link (and timeframe since D1 — TV Interval selective sync, spec §4)
        if (type.startsWith('visible-range') || type.startsWith('crosshair') || type.startsWith('symbol-link') || type.startsWith('timeframe')) {
            let sourceGroup = null;
            for (const [gid, group] of stateRef.current.symbolLinkGroups.entries()) {
                if (group.chartIds.has(meta.sourceChartId)) {
                    sourceGroup = gid;
                    break;
                }
            }
            if (sourceGroup) {
                const group = stateRef.current.symbolLinkGroups.get(sourceGroup);
                if (group) {
                    targets = targets.filter(id => group.chartIds.has(id));
                }
            }
            else {
                // If source chart is not in any group, it only syncs with other charts that are also NOT in any group
                targets = targets.filter(id => {
                    let hasGroup = false;
                    for (const group of stateRef.current.symbolLinkGroups.values()) {
                        if (group.chartIds.has(id)) {
                            hasGroup = true;
                            break;
                        }
                    }
                    return !hasGroup;
                });
            }
        }
        // Throttle per domain: leading + trailing coalescing via pendingTasksRef
        // (one pending task per domain+source, flushed on clockAdapter frames;
        // cross-tab leg has its own budget — see getCrossTabThrottleMs, spec §3)
        targets.forEach(chartId => {
            const chart = charts.get(chartId);
            if (!chart)
                return;
            switch (type) {
                case 'visible-range:update': {
                    const stamped = stampEchoMeta(payload, meta);
                    lastAppliedSequenceRef.current.set(`${chartId}:visibleRange`, meta.sequence);
                    chart.subscriptions.visibleRange.forEach(cb => cb(stamped));
                    chart.subscriptions.syncViewport.forEach(cb => cb(stamped));
                    chart.handle?.setVisibleRange?.(stamped, meta.sourceChartId);
                    break;
                }
                case 'visible-range:request':
                    // F2: late-joiner catch-up — notify subscribers (in-tab only; the
                    // cross-tab leg skips request types, spec §6). No group gate: any
                    // chart may ask; responders decide (widget: active-only).
                    chart.subscriptions.visibleRangeRequest.forEach(cb => cb(meta.sourceChartId));
                    break;
                case 'crosshair:update': {
                    const stamped = stampEchoMeta(payload, meta);
                    lastAppliedSequenceRef.current.set(`${chartId}:crosshair`, meta.sequence);
                    chart.subscriptions.crosshair.forEach(cb => cb(stamped));
                    chart.subscriptions.syncViewport.forEach(cb => cb(stamped));
                    chart.handle?.setCrosshair?.(stamped, meta.sourceChartId);
                    break;
                }
                case 'crosshair:clear':
                    chart.subscriptions.crosshair.forEach(cb => cb(null));
                    chart.handle?.clearCrosshair?.();
                    break;
                case 'time:anchor':
                    // F1: fire-and-forget, handle-only (no subscriptions, no meta, no
                    // group gate — TV Time toggle is global). Source excluded by fan-out.
                    chart.handle?.setTimeAnchor?.(payload, meta.sourceChartId);
                    break;
                case 'drawing:create':
                case 'drawing:update':
                case 'drawing:delete':
                    chart.subscriptions.drawings.forEach(cb => cb(getDrawingsForChart(chartId)));
                    chart.subscriptions.syncDrawing.forEach(cb => cb(payload));
                    chart.handle?.syncDrawing?.(payload);
                    break;
                case 'timeframe:update':
                    chart.subscriptions.timeframe.forEach(cb => cb(payload.timeframe));
                    chart.handle?.setTimeframe?.(payload.timeframe, payload.sourceChartId);
                    break;
                case 'chart-type:update':
                    chart.subscriptions.chartType.forEach(cb => cb(payload.chartType));
                    chart.handle?.setChartType?.(payload.chartType, meta.sourceChartId);
                    break;
                case 'drawing-tool:update':
                    chart.subscriptions.drawingTool.forEach(cb => cb(payload));
                    break;
                case 'indicator:update':
                    chart.subscriptions.indicators.forEach(cb => cb(payload.indicators));
                    break;
            }
        });
        // Cross-tab broadcast via adapter (throttled per spec §3 — Q8: 50ms crosshair).
        // 'visible-range:request' stays in-tab (F2, spec §6): it would otherwise
        // leak across tabs disguised as an (empty) range update. Other *:request
        // stubs map to unhandled cross-tab types and are ignored by receivers.
        const channel = stateRef.current.crossTabChannel;
        if (channel && channel.isAvailable() && type !== 'visible-range:request') {
            const domainKey = CROSS_TAB_DOMAIN_MAP[type.split(':')[0]] ?? 'drawing';
            const budgetMs = getCrossTabThrottleMs(domainKey);
            const now = getTime();
            const lastSent = lastCrossTabSentRef.current.get(domainKey) ?? -Infinity;
            // SYNC-1/A+ (replaces P2/D-SYNC-16 delete-only bypass): discrete
            // lifecycle ops (create/delete) bypass the throttle without consuming
            // it. A create/delete is user-discrete (never a 60fps storm) and
            // terminal for convergence — dropping one orphaned the drawing on
            // sibling tabs permanently (GAP-CROSSTAB-CREATE-DROP). Duplicate
            // discrete ops are harmless (CRDT version compare dedups).
            const opKind = payload?.type;
            const isDrawingDiscrete = type === 'drawing:update'
                && (opKind === 'create' || opKind === 'delete');
            const postCrossTab = () => {
                lastCrossTabSentRef.current.set(domainKey, getTime());
                const crossTabMessage = {
                    type: type.split(':')[0],
                    payload,
                    workspaceId: stateRef.current.workspaceId || '',
                    sourceTabId: '', // Will be filled by adapter
                    timestamp: now,
                };
                channel.postMessage(crossTabMessage);
            };
            if (isDrawingDiscrete || budgetMs <= 0 || now - lastSent >= budgetMs) {
                // Discrete ops bypass without consuming the budget (delete semantics
                // preserved from P2); leading posts consume it as before.
                if (!isDrawingDiscrete)
                    lastCrossTabSentRef.current.set(domainKey, now);
                const crossTabMessage = {
                    type: type.split(':')[0],
                    payload,
                    workspaceId: stateRef.current.workspaceId || '',
                    sourceTabId: '', // Will be filled by adapter
                    timestamp: now,
                };
                channel.postMessage(crossTabMessage);
            }
            else if (type === 'drawing:update' && opKind === 'update') {
                // SYNC-1/A+: trailing retransmit for the continuous gesture stream
                // only. Leading + trailing coalescing via pendingTasksRef — the same
                // pattern as setVisibleRange/setCrosshair. Intermediate drag positions
                // may coalesce (latest wins, CRDT-convergent), but the final position
                // always lands within ~2x budget. Keyed per-domain (the cross-tab
                // budget itself is per-domain), NOT per-chart: unregisterChart cleanup
                // (keys `${domain}:${chartId}`) intentionally leaves this slot alone —
                // it self-cleans after one flush.
                const key = `cross-tab:${domainKey}`;
                const tasks = pendingTasksRef.current;
                const existing = tasks.get(key);
                if (existing) {
                    existing.callback = postCrossTab;
                    existing.hasTrailing = true;
                }
                else {
                    // NOTE: differs from setVisibleRange/setCrosshair, where the task
                    // is created ALONGSIDE an immediate post of the same data
                    // (hasTrailing:false correct there). Here the task is created FOR
                    // an op that was just throttled UNDELIVERED, so the slot starts
                    // dirty (hasTrailing:true) — otherwise a lone throttled update
                    // with no successor in the window would be dropped at flush.
                    const scheduledTime = now + budgetMs;
                    const tick = () => {
                        const task = tasks.get(key);
                        if (!task)
                            return;
                        const currentTime = getTime();
                        if (currentTime >= task.scheduledTime) {
                            tasks.delete(key);
                            if (task.hasTrailing) {
                                task.callback();
                            }
                        }
                        else {
                            task.frameId = clockAdapter.requestFrame(tick);
                        }
                    };
                    const frameId = clockAdapter.requestFrame(tick);
                    tasks.set(key, { callback: postCrossTab, scheduledTime, frameId, hasTrailing: true });
                }
            }
        }
    }, [getTime, getCrossTabThrottleMs]);
    // --- Drawing CRDT Helpers ---
    const getDrawingStateKey = (symbol, timeframe) => `${symbol}:${timeframe}`;
    const getOrCreateDrawingState = (symbol, timeframe) => {
        const key = getDrawingStateKey(symbol, timeframe);
        let state = stateRef.current.drawingState.get(key);
        if (!state) {
            state = { operations: new Map(), versionVector: {}, drawings: new Map() };
            stateRef.current.drawingState.set(key, state);
        }
        return state;
    };
    // P2: resolve the scope an op belongs to. Complete data scope wins, then a
    // scan of known states (legacy + delete/tombstone paths). An explicit
    // op-level symbol stamp (GridContainer E1) that disagrees with the found
    // scope drops the op (D-SYNC-24: no cross-symbol deletes).
    const resolveDrawingOpScope = (op) => {
        const data = op.data;
        if (data && typeof data.symbol === 'string' && data.symbol
            && typeof data.timeframe === 'string' && data.timeframe) {
            return { symbol: data.symbol, timeframe: data.timeframe };
        }
        const stamped = op.symbol;
        const effectiveSymbol = typeof stamped === 'string' && stamped
            ? stamped
            : (data && typeof data.symbol === 'string' ? data.symbol : undefined);
        for (const [key, state] of stateRef.current.drawingState.entries()) {
            if (state.drawings.has(op.drawingId) || state.operations.has(op.drawingId)) {
                const sep = key.indexOf(':');
                const found = { symbol: key.slice(0, sep), timeframe: key.slice(sep + 1) };
                if (effectiveSymbol && effectiveSymbol !== found.symbol)
                    continue;
                return found;
            }
        }
        return null;
    };
    // P2 tombstone ordering: with empty version vectors everywhere (widget emits
    // version {}), recency decides. A create/update older-or-equal to a recorded
    // delete loses; a newer one clears the tombstone (re-draw after delete).
    const isOpSupersededByTombstone = (op, tomb) => {
        const compare = compareVersionVectors(op.version ?? {}, tomb.version ?? {});
        if (compare !== 0)
            return compare < 0;
        return (op.timestamp ?? 0) <= (tomb.timestamp ?? 0);
    };
    const applyOperation = (op) => {
        const { drawingId, version, data, authorId } = op;
        // D-SYNC-17: move-point carries point updates — apply exactly like update
        // so the shadow CRDT never drifts from the dragged UI state.
        const type = op.type === 'move-point' ? 'update' : op.type;
        if (op.type === 'delete') {
            // D-SYNC-16: deletes always record a scope-free tombstone first, so even
            // a delete that arrives before its create (cross-author) wins ordering.
            const existing = stateRef.current.drawingTombstones.get(drawingId);
            if (!existing || !isOpSupersededByTombstone(op, existing)) {
                stateRef.current.drawingTombstones.set(drawingId, op);
            }
        }
        else {
            const tomb = stateRef.current.drawingTombstones.get(drawingId);
            if (tomb && isOpSupersededByTombstone(op, tomb)) {
                const scope = resolveDrawingOpScope(op);
                if (scope) {
                    const state = getOrCreateDrawingState(scope.symbol, scope.timeframe);
                    state.versionVector = mergeVersionVectors(state.versionVector, version ?? {});
                }
                return;
            }
            stateRef.current.drawingTombstones.delete(drawingId);
        }
        const scope = resolveDrawingOpScope(op);
        if (!scope)
            return;
        const { symbol, timeframe } = scope;
        {
            const state = getOrCreateDrawingState(symbol, timeframe);
            const existingOp = state.operations.get(drawingId);
            const compare = existingOp ? compareVersionVectors(version ?? {}, existingOp.version) : 1;
            // S4.3/F-21: same condition drives both the conflict callback and the
            // apply decision, so winner + stored op always agree.
            const remoteWinsTied = compare === 0 && !!existingOp && isRemoteWinner(op, existingOp);
            if (compare === 0 && existingOp) {
                // S4.3/F-21: deterministic tie-break — equal timestamps resolve by
                // lexicographically SMALLER authorId, independent of arrival order
                // (two tabs converge to the same winner either way around).
                const winner = remoteWinsTied ? 'remote' : 'local';
                const localOp = remoteWinsTied ? existingOp : op;
                const remoteOp = remoteWinsTied ? op : existingOp;
                stateRef.current.drawingConflictCallbacks.forEach(cb => {
                    try {
                        cb({ drawingId, localOp, remoteOp, winner });
                    }
                    catch (_) { /* swallow */ }
                });
            }
            if (!existingOp || compare > 0 || remoteWinsTied) {
                state.operations.set(drawingId, op);
                if (type === 'create' && data) {
                    state.drawings.set(drawingId, data);
                }
                else if (type === 'update' && data) {
                    const existing = state.drawings.get(drawingId);
                    if (existing)
                        state.drawings.set(drawingId, { ...existing, ...data });
                }
                else if (type === 'delete') {
                    state.drawings.delete(drawingId);
                }
            }
            state.versionVector = mergeVersionVectors(state.versionVector, version ?? {});
        }
    };
    // S4.3/F-21: LWW with deterministic authorId tie-break. Strictly newer
    // timestamp wins (raw `>` — unchanged semantics); on EQUAL timestamps the
    // lexicographically smaller authorId wins, so two tabs applying the same
    // ops in different arrival orders converge. authorId falls back to ""
    // (stable comparison, never undefined-crash).
    const isRemoteWinner = (op, existingOp) => {
        if (op.timestamp === existingOp.timestamp) {
            return (op.authorId ?? "") < (existingOp.authorId ?? "");
        }
        return op.timestamp > existingOp.timestamp;
    };
    const compareVersionVectors = (a, b) => {
        const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
        let aGreater = false;
        let bGreater = false;
        for (const key of allKeys) {
            const av = a[key] || 0;
            const bv = b[key] || 0;
            if (av > bv)
                aGreater = true;
            if (bv > av)
                bGreater = true;
        }
        if (aGreater && !bGreater)
            return 1;
        if (bGreater && !aGreater)
            return -1;
        return 0; // concurrent
    };
    const mergeVersionVectors = (a, b) => {
        const result = { ...a };
        for (const [key, value] of Object.entries(b)) {
            result[key] = Math.max(result[key] || 0, value);
        }
        return result;
    };
    const getDrawingsForChart = (chartId) => {
        const chart = stateRef.current.charts.get(chartId);
        if (!chart)
            return [];
        // Filter drawings by chart's symbol/timeframe
        const key = getDrawingStateKey(chart.handle.symbol, chart.handle.timeframe);
        const state = stateRef.current.drawingState.get(key);
        return state ? Array.from(state.drawings.values()) : [];
    };
    // --- Cross-tab Sync ---
    // R2-b: workspace loader extracted (used by the value API and by the
    // cross-tab receiver below). notifyTabs:false breaks reload loops.
    const loadWorkspace = useCallback(async (workspaceId, opts) => {
        if (storageAdapter) {
            const raw = await storageAdapter.getItem(workspaceId);
            if (raw) {
                try {
                    const workspace = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    if (workspace && workspace.charts) {
                        workspace.charts.forEach((chartState) => {
                            const chart = stateRef.current.charts.get(chartState.id || chartState.chartId);
                            if (chart) {
                                if (chartState.symbol)
                                    chart.handle.symbol = chartState.symbol;
                                if (chartState.timeframe)
                                    chart.handle.timeframe = chartState.timeframe;
                                if (chartState.visibleRange) {
                                    chart.handle.setVisibleRange(chartState.visibleRange);
                                }
                            }
                        });
                    }
                    if (opts?.notifyTabs !== false) {
                        const channel = stateRef.current.crossTabChannel;
                        if (channel && channel.isAvailable()) {
                            channel.postMessage({
                                type: 'workspace',
                                payload: { action: 'load', workspaceId },
                                workspaceId: stateRef.current.workspaceId || '',
                                sourceTabId: '',
                                timestamp: getTime(),
                            });
                        }
                    }
                }
                catch (e) {
                    console.error("Failed to parse workspace JSON", e);
                }
            }
        }
    }, [storageAdapter, getTime]);
    const enableCrossTabSync = useCallback((wsId) => {
        // broadcastChannelAdapter is required for cross-tab sync
        if (!broadcastChannelAdapter) {
            console.warn('SyncContext: broadcastChannelAdapter not provided, cross-tab sync disabled');
            return;
        }
        stateRef.current.crossTabChannel = broadcastChannelAdapter;
        stateRef.current.workspaceId = wsId;
        // Subscribe to cross-tab messages
        broadcastChannelAdapter.onMessage((message) => {
            // Don't process our own messages (adapter handles this)
            // Re-broadcast locally to subscribed charts
            const { charts } = stateRef.current;
            const targets = Array.from(charts.keys());
            let type = message.type;
            let payload = message.payload;
            if (type === 'visibleRange') {
                type = 'visible-range';
                // R2-c (SY-17): single payload shape — senders post the in-tab payload
                // as-is. Legacy `{ range }` / `{ crosshair }` wrappers are NOT unwrapped
                // anymore (no prod sender produces them; they only matched old mocks).
            }
            if (type === 'drawing') {
                // P2: accept both the live DrawingOperation shape (syncDrawing
                // broadcasts the op itself) and the legacy { drawing, action } shape.
                // Pre-P2 the live shape fell through silently — cross-tab drawing sync
                // (including every delete) never applied on the receiving tab.
                const op = normalizeCrossTabDrawingOp(payload, message.sourceTabId, getTime());
                if (op)
                    applyOperation(op);
            }
            targets.forEach(chartId => {
                const chart = charts.get(chartId);
                if (!chart)
                    return;
                switch (type) {
                    case 'visible-range':
                        chart.subscriptions.visibleRange.forEach(cb => cb(payload));
                        chart.subscriptions.syncViewport.forEach(cb => cb(payload));
                        break;
                    case 'crosshair':
                        chart.subscriptions.crosshair.forEach(cb => cb(payload));
                        chart.subscriptions.syncViewport.forEach(cb => cb(payload));
                        break;
                    case 'drawing': {
                        chart.subscriptions.drawings.forEach(cb => cb(getDrawingsForChart(chartId)));
                        // P2: forward a normalized op (either payload shape) to
                        // syncDrawing subscribers; pre-P2 only the legacy shape produced one.
                        const op = normalizeCrossTabDrawingOp(payload, message.sourceTabId, getTime());
                        if (op)
                            chart.subscriptions.syncDrawing.forEach(cb => cb(op));
                        break;
                    }
                    case 'symbol-link': {
                        const { groupId, symbol } = payload;
                        const group = stateRef.current.symbolLinkGroups.get(groupId);
                        if (group) {
                            group.symbol = symbol;
                        }
                        const callbacks = stateRef.current.groupSymbolCallbacks.get(groupId);
                        if (callbacks) {
                            callbacks.forEach(cb => cb(symbol));
                        }
                        break;
                    }
                    case 'workspace': {
                        // R2-b: a sibling tab loaded a workspace — reload it locally so all
                        // tabs converge. Guarded by workspaceId; notifyTabs:false breaks the
                        // reload loop (our own load does not re-broadcast).
                        const action = payload?.action;
                        const incomingId = payload?.workspaceId;
                        if (action === 'load' && typeof incomingId === 'string' && incomingId.length > 0
                            && (stateRef.current.workspaceId === null || incomingId === stateRef.current.workspaceId)) {
                            void loadWorkspace(incomingId, { notifyTabs: false });
                        }
                        break;
                    }
                }
            });
            triggerUpdate();
        });
    }, [broadcastChannelAdapter, triggerUpdate, loadWorkspace]);
    const disableCrossTabSync = useCallback(() => {
        stateRef.current.crossTabChannel?.close();
        stateRef.current.crossTabChannel = null;
    }, []);
    // --- SyncContextValue Implementation ---
    const value = useMemo(() => ({
        // Registration
        registerChart: (chartId, handle) => {
            const existing = stateRef.current.charts.get(chartId);
            if (existing) {
                // R3-a: re-mounts (StrictMode, layout flips) must not silently keep a
                // stale handle with dead closures — migrate it, keep groups/subs.
                if (existing.handle !== handle) {
                    console.warn(`[SyncContext] Chart "${chartId}" re-registered with a new handle. Migrating (groups/subscriptions preserved).`);
                    existing.handle = handle;
                }
                return;
            }
            stateRef.current.charts.set(chartId, {
                handle,
                subscriptions: {
                    visibleRange: new Set(),
                    visibleRangeRequest: new Set(),
                    crosshair: new Set(),
                    drawings: new Set(),
                    syncDrawing: new Set(),
                    syncViewport: new Set(),
                    timeframe: new Set(),
                    chartType: new Set(),
                    drawingTool: new Set(),
                    indicators: new Set(),
                },
            });
        },
        unregisterChart: (chartId) => {
            // R3-b: full cleanup — a half-removed chart must not receive stale
            // throttle callbacks, group fan-outs, or stay elected (active/responder).
            stateRef.current.charts.delete(chartId);
            // Cancel pending throttle tasks for this chart (keys `${domain}:${chartId}`).
            for (const [key, task] of pendingTasksRef.current) {
                if (key.endsWith(`:${chartId}`)) {
                    if (task.frameId != null) {
                        clockAdapter.cancelFrame(task.frameId);
                    }
                    pendingTasksRef.current.delete(key);
                }
            }
            // Detach from symbol-link groups + indicator registry.
            for (const group of stateRef.current.symbolLinkGroups.values()) {
                group.chartIds.delete(chartId);
            }
            stateRef.current.indicatorState.delete(chartId);
            // Reset active tracking (symmetric notify, like setActiveChart).
            if (stateRef.current.activeChartId === chartId) {
                stateRef.current.activeChartId = null;
                stateRef.current.activeChartSubscriptions.forEach(cb => cb(null));
            }
        },
        // Visible Range
        setVisibleRange: (chartId, range) => {
            // Loop-guard (spec §5, A3): drop echoes of remotely-applied events.
            // Null-safe: clear-paths may forward null payloads.
            if (isEchoEvent(chartId, 'visibleRange', range?.meta))
                return;
            const throttleMs = getThrottleMs('visibleRange');
            const key = `visible-range:${chartId}`;
            const runBroadcast = () => {
                const meta = createMeta(chartId);
                broadcast('visible-range:update', range, meta);
            };
            const tasks = pendingTasksRef.current;
            const existing = tasks.get(key);
            const now = clockAdapter.now();
            if (existing) {
                existing.callback = runBroadcast;
                existing.hasTrailing = true;
            }
            else {
                runBroadcast();
                const scheduledTime = now + throttleMs;
                const tick = () => {
                    const task = tasks.get(key);
                    if (!task)
                        return;
                    const currentTime = clockAdapter.now();
                    if (currentTime >= task.scheduledTime) {
                        tasks.delete(key);
                        if (task.hasTrailing) {
                            task.callback();
                        }
                    }
                    else {
                        task.frameId = clockAdapter.requestFrame(tick);
                    }
                };
                const frameId = clockAdapter.requestFrame(tick);
                tasks.set(key, { callback: runBroadcast, scheduledTime, frameId, hasTrailing: false });
            }
        },
        requestVisibleRange: (chartId) => {
            const meta = createMeta(chartId);
            broadcast('visible-range:request', {}, meta);
        },
        onVisibleRangeChange: (chartId, callback) => {
            const chart = stateRef.current.charts.get(chartId);
            if (!chart)
                return () => { };
            chart.subscriptions.visibleRange.add(callback);
            return () => chart.subscriptions.visibleRange.delete(callback);
        },
        // Late-joiner catch-up (F2): notifies when another chart asks for ranges.
        onVisibleRangeRequest: (chartId, callback) => {
            const chart = stateRef.current.charts.get(chartId);
            if (!chart)
                return () => { };
            chart.subscriptions.visibleRangeRequest.add(callback);
            return () => chart.subscriptions.visibleRangeRequest.delete(callback);
        },
        // Targeted catch-up delivery (F2/Q5): delivers one chart's range straight
        // to the requester — no broadcast storm, no throttle coupling (answers must
        // land synchronously inside the request cascade), no cross-tab leak.
        respondVisibleRange: (requesterId, responderId, range) => {
            const target = stateRef.current.charts.get(requesterId);
            if (!target)
                return;
            const meta = createMeta(responderId);
            const stamped = stampEchoMeta(range, meta);
            lastAppliedSequenceRef.current.set(`${requesterId}:visibleRange`, meta.sequence);
            target.handle?.setVisibleRange?.(stamped, responderId);
        },
        // Crosshair
        setCrosshair: (chartId, crosshair) => {
            // Loop-guard (spec §5, A3): drop echoes of remotely-applied events.
            // Null-safe: clear-paths may forward null payloads.
            if (isEchoEvent(chartId, 'crosshair', crosshair?.meta))
                return;
            const throttleMs = getThrottleMs('crosshair');
            const key = `crosshair:${chartId}`;
            const runBroadcast = () => {
                const meta = createMeta(chartId);
                broadcast('crosshair:update', crosshair, meta);
            };
            const tasks = pendingTasksRef.current;
            const existing = tasks.get(key);
            const now = clockAdapter.now();
            if (existing) {
                existing.callback = runBroadcast;
                existing.hasTrailing = true;
            }
            else {
                runBroadcast();
                const scheduledTime = now + throttleMs;
                const tick = () => {
                    const task = tasks.get(key);
                    if (!task)
                        return;
                    const currentTime = clockAdapter.now();
                    if (currentTime >= task.scheduledTime) {
                        tasks.delete(key);
                        if (task.hasTrailing) {
                            task.callback();
                        }
                    }
                    else {
                        task.frameId = clockAdapter.requestFrame(tick);
                    }
                };
                const frameId = clockAdapter.requestFrame(tick);
                tasks.set(key, { callback: runBroadcast, scheduledTime, frameId, hasTrailing: false });
            }
        },
        clearCrosshair: (chartId) => {
            const meta = createMeta(chartId);
            broadcast('crosshair:clear', {}, meta);
        },
        // Time anchor (F1): discrete jump, no throttle, no group gate.
        setTimeAnchor: (chartId, timestamp) => {
            const meta = createMeta(chartId);
            broadcast('time:anchor', { timestamp }, meta);
        },
        onCrosshairChange: (chartId, callback) => {
            const chart = stateRef.current.charts.get(chartId);
            if (!chart)
                return () => { };
            chart.subscriptions.crosshair.add(callback);
            return () => chart.subscriptions.crosshair.delete(callback);
        },
        // Symbol Link Groups
        assignToGroup: (chartId, groupId) => {
            // Remove from current group
            for (const [gid, group] of stateRef.current.symbolLinkGroups.entries()) {
                group.chartIds.delete(chartId);
            }
            // Assign to new group
            if (groupId) {
                let group = stateRef.current.symbolLinkGroups.get(groupId);
                if (!group) {
                    group = { symbol: '', chartIds: new Set() };
                    stateRef.current.symbolLinkGroups.set(groupId, group);
                }
                group.chartIds.add(chartId);
                // Broadcast symbol to group
                const meta = createMeta(chartId, undefined, groupId);
                broadcast('symbol-link:update', { groupId, symbol: group.symbol }, meta);
            }
            triggerUpdate();
        },
        setGroupSymbol: (groupId, symbol) => {
            const group = stateRef.current.symbolLinkGroups.get(groupId);
            if (!group)
                return;
            group.symbol = symbol;
            const meta = createMeta('system', Array.from(group.chartIds), groupId);
            broadcast('symbol-link:update', { groupId, symbol }, meta);
            const callbacks = stateRef.current.groupSymbolCallbacks.get(groupId);
            if (callbacks) {
                callbacks.forEach(cb => cb(symbol));
            }
        },
        getGroupCharts: (groupId) => {
            const group = stateRef.current.symbolLinkGroups.get(groupId);
            return group ? Array.from(group.chartIds) : [];
        },
        onGroupSymbolChange: (groupId, callback) => {
            let callbacks = stateRef.current.groupSymbolCallbacks.get(groupId);
            if (!callbacks) {
                callbacks = new Set();
                stateRef.current.groupSymbolCallbacks.set(groupId, callbacks);
            }
            callbacks.add(callback);
            return () => {
                const cbs = stateRef.current.groupSymbolCallbacks.get(groupId);
                if (cbs) {
                    cbs.delete(callback);
                }
            };
        },
        // Drawings (CRDT)
        applyDrawingOperation: (op) => {
            applyOperation(op);
            // P2-06: notify only same-scope charts instead of every slot, so a drag
            // storm on one symbol does not re-render unrelated charts. Unresolvable
            // scope falls back to notifying all (legacy behavior).
            const scope = resolveDrawingOpScope(op);
            for (const [chartId, chart] of stateRef.current.charts.entries()) {
                if (scope) {
                    const handle = chart.handle;
                    if (handle.symbol !== scope.symbol || handle.timeframe !== scope.timeframe)
                        continue;
                }
                chart.subscriptions.drawings.forEach(cb => cb(getDrawingsForChart(chartId)));
            }
        },
        onDrawingChange: (chartId, callback) => {
            const chart = stateRef.current.charts.get(chartId);
            if (!chart)
                return () => { };
            chart.subscriptions.drawings.add(callback);
            return () => chart.subscriptions.drawings.delete(callback);
        },
        // Sync Drawing - broadcast drawing operations to other charts in sync group
        syncDrawing: (operation) => {
            // Source identity (A3): originating chart first, so fan-out excludes it
            // (no self-echo). authorId is a user/session id — NOT a chart id.
            const sourceChartId = operation.chartId || operation.authorId || 'system';
            // P2/D-SYNC-15: converge the local CRDT first so in-tab subscribers
            // (getDrawingsForChart) read fresh state; fan-out below then notifies.
            try {
                applyOperation(operation);
            }
            catch (_) { /* a bad op must never break the broadcast */ }
            const meta = createMeta(sourceChartId);
            broadcast('drawing:update', operation, meta);
        },
        onSyncDrawing: (chartId, callback) => {
            const chart = stateRef.current.charts.get(chartId);
            if (!chart)
                return () => { };
            chart.subscriptions.syncDrawing.add(callback);
            return () => chart.subscriptions.syncDrawing.delete(callback);
        },
        // Sync Viewport - broadcast viewport changes (visible range + crosshair) to other charts
        syncViewport: (payload) => {
            // Determine if it's a visible range or crosshair payload
            const isVisibleRange = 'timeRange' in payload;
            const type = isVisibleRange ? 'visible-range:update' : 'crosshair:update';
            // Source identity (A3): preserve the real origin when known (echo stamp
            // from a prior fan-out, or an explicit chart id) instead of 'system',
            // so the source chart is excluded and echoes stay attributable.
            const stamped = payload;
            const sourceChartId = stamped.meta?.sourceChartId || stamped.chartId || stamped.sourceChartId || 'system';
            const meta = createMeta(sourceChartId);
            broadcast(type, payload, meta);
        },
        onSyncViewport: (chartId, callback) => {
            const chart = stateRef.current.charts.get(chartId);
            if (!chart)
                return () => { };
            chart.subscriptions.syncViewport.add(callback);
            return () => chart.subscriptions.syncViewport.delete(callback);
        },
        // Workspace
        saveWorkspace: async (workspaceData) => {
            const throttleMs = getThrottleMs('workspace');
            const key = 'workspace:save';
            const runSave = () => {
                if (storageAdapter) {
                    const workspaceId = stateRef.current.workspaceId || 'default-workspace';
                    storageAdapter.setItem(workspaceId, workspaceData);
                }
            };
            const tasks = pendingTasksRef.current;
            const existing = tasks.get(key);
            const now = clockAdapter.now();
            if (existing) {
                existing.callback = runSave;
            }
            else {
                const scheduledTime = now + throttleMs;
                const tick = () => {
                    const task = tasks.get(key);
                    if (!task)
                        return;
                    const currentTime = clockAdapter.now();
                    if (currentTime > task.scheduledTime) {
                        tasks.delete(key);
                        task.callback();
                    }
                    else {
                        task.frameId = clockAdapter.requestFrame(tick);
                    }
                };
                const frameId = clockAdapter.requestFrame(tick);
                tasks.set(key, { callback: runSave, scheduledTime, frameId });
            }
        },
        loadWorkspace,
        // Active Chart Tracking
        setActiveChart: (chartId) => {
            stateRef.current.activeChartId = chartId;
            stateRef.current.activeChartSubscriptions.forEach(cb => cb(chartId));
        },
        getActiveChart: () => {
            return stateRef.current.activeChartId;
        },
        onActiveChartChange: (callback) => {
            stateRef.current.activeChartSubscriptions.add(callback);
            return () => stateRef.current.activeChartSubscriptions.delete(callback);
        },
        // Timeframe Sync
        setTimeframe: (chartId, timeframe) => {
            const throttleMs = getThrottleMs('timeframe');
            const key = `timeframe:${chartId}`;
            const runBroadcast = () => {
                const meta = createMeta(chartId);
                broadcast('timeframe:update', { chartId, timeframe, sourceChartId: meta.sourceChartId }, meta);
            };
            const tasks = pendingTasksRef.current;
            const existing = tasks.get(key);
            const now = clockAdapter.now();
            if (existing) {
                existing.callback = runBroadcast;
                existing.hasTrailing = true;
            }
            else {
                runBroadcast();
                const scheduledTime = now + throttleMs;
                const tick = () => {
                    const task = tasks.get(key);
                    if (!task)
                        return;
                    const currentTime = clockAdapter.now();
                    if (currentTime >= task.scheduledTime) {
                        tasks.delete(key);
                        if (task.hasTrailing) {
                            task.callback();
                        }
                    }
                    else {
                        task.frameId = clockAdapter.requestFrame(tick);
                    }
                };
                const frameId = clockAdapter.requestFrame(tick);
                tasks.set(key, { callback: runBroadcast, scheduledTime, frameId, hasTrailing: false });
            }
        },
        requestTimeframe: (chartId) => {
            const meta = createMeta(chartId);
            broadcast('timeframe:request', { chartId }, meta);
        },
        onTimeframeChange: (chartId, callback) => {
            const chart = stateRef.current.charts.get(chartId);
            if (!chart)
                return () => { };
            chart.subscriptions.timeframe.add(callback);
            return () => chart.subscriptions.timeframe.delete(callback);
        },
        // Chart Type Sync — LOCAL-ONLY since D2a (Q1, TV parity).
        // chart-type:update is retired from auto-broadcast: each chart owns its
        // type through its own UI. This method is retained (deprecated) as the
        // future explicit "apply to entire layout" action entry-point (D2b).
        setChartType: (_chartId, _chartType) => {
            // Intentionally no broadcast, no throttle task, no subscription notify.
        },
        requestChartType: (chartId) => {
            const meta = createMeta(chartId);
            broadcast('chart-type:request', { chartId }, meta);
        },
        onChartTypeChange: (chartId, callback) => {
            const chart = stateRef.current.charts.get(chartId);
            if (!chart)
                return () => { };
            chart.subscriptions.chartType.add(callback);
            return () => chart.subscriptions.chartType.delete(callback);
        },
        // Drawing Tool Sync
        setDrawingTool: (chartId, tool) => {
            const throttleMs = getThrottleMs('drawingTool');
            const key = `drawing-tool:${chartId}`;
            const runBroadcast = () => {
                const meta = createMeta(chartId);
                broadcast('drawing-tool:update', tool, meta);
            };
            const tasks = pendingTasksRef.current;
            const existing = tasks.get(key);
            const now = clockAdapter.now();
            if (existing) {
                existing.callback = runBroadcast;
                existing.hasTrailing = true;
            }
            else {
                runBroadcast();
                const scheduledTime = now + throttleMs;
                const tick = () => {
                    const task = tasks.get(key);
                    if (!task)
                        return;
                    const currentTime = clockAdapter.now();
                    if (currentTime >= task.scheduledTime) {
                        tasks.delete(key);
                        if (task.hasTrailing) {
                            task.callback();
                        }
                    }
                    else {
                        task.frameId = clockAdapter.requestFrame(tick);
                    }
                };
                const frameId = clockAdapter.requestFrame(tick);
                tasks.set(key, { callback: runBroadcast, scheduledTime, frameId, hasTrailing: false });
            }
        },
        requestDrawingTool: (chartId) => {
            const meta = createMeta(chartId);
            broadcast('drawing-tool:request', { chartId }, meta);
        },
        onDrawingToolChange: (chartId, callback) => {
            const chart = stateRef.current.charts.get(chartId);
            if (!chart)
                return () => { };
            chart.subscriptions.drawingTool.add(callback);
            return () => chart.subscriptions.drawingTool.delete(callback);
        },
        // Drawing conflict notification
        onDrawingConflict: (callback) => {
            stateRef.current.drawingConflictCallbacks.add(callback);
            return () => { stateRef.current.drawingConflictCallbacks.delete(callback); };
        },
        // Indicator sync — LOCAL-ONLY since D2a (Q1, TV parity).
        // indicator:update is retired from auto-broadcast. The per-chart registry
        // write is kept (D2b explicit apply reads it); no fan-out, no throttle task.
        setIndicators: (chartId, indicators) => {
            stateRef.current.indicatorState.set(chartId, indicators);
        },
        onIndicatorsChange: (chartId, callback) => {
            const chart = stateRef.current.charts.get(chartId);
            if (!chart)
                return () => { };
            chart.subscriptions.indicators.add(callback);
            return () => { chart.subscriptions.indicators.delete(callback); };
        },
        // Cross-tab
        enableCrossTabSync,
        disableCrossTabSync,
    }), [
        createMeta,
        broadcast,
        enableCrossTabSync,
        disableCrossTabSync,
        triggerUpdate,
        getThrottleMs,
    ]);
    // --- Lifecycle & Cleanup ---
    useEffect(() => {
        if (workspaceId) {
            enableCrossTabSync(workspaceId);
        }
        return () => {
            disableCrossTabSync();
        };
    }, [workspaceId, enableCrossTabSync, disableCrossTabSync]);
    return (_jsx(SyncContext.Provider, { value: value, children: children }));
};
export default SyncProvider;
